import React, {
  useEffect, useState, useContext, useRef,
} from 'react';
import PropTypes from 'prop-types';

import AppContext from '../../context';
import logger from '../../utils/logger';
import {
  getLocalStorageTablePref, updateTablePreferenceLocalStorage, tableColumnPreferenceLoader,
} from '../../utils/index';
import APIWrapper from '../../utils/graphqlwrapper';
import { importHistoryColumns } from '../../utils/schemaInfo';

import Table from '../Table';
import { createImportHistoryRows } from '../../utils/tableHelpers';

const initPageState = {
  isLoading: true,
  page: 0, // mui pages are 0 indexed
  pageSize: 50,
  data: [],
  total: 0,
};

const defaultRowSort = [{ field: 'updatedAt', sort: 'desc' }];

const getTablePrefix = (type) => (type === 'p2p_bulk_upload' ? 'p2pbulkimporthistory' : 'importhistory');

const P2P_EXCLUDED_FIELDS = new Set(['exclusionCount', 'inclusionCount']);
const EXCLUSIONS_EXCLUDED_FIELDS = new Set(['count']);

const getColumnsForType = (type) => {
  if (type === 'p2p_bulk_upload') {
    return importHistoryColumns.filter((col) => !P2P_EXCLUDED_FIELDS.has(col.field));
  }
  return importHistoryColumns.filter((col) => !EXCLUSIONS_EXCLUDED_FIELDS.has(col.field));
};

const getInitColumnVisibilityModel = () => ({ createdAt: false, updatedAt: false });

export default function ImportHistory(props) {
  const {
    client, importedNewData, handleRefreshHandled, type,
  } = props;

  const tablePrefix = getTablePrefix(type);
  const initColumnVisibilityModel = getInitColumnVisibilityModel();

  const { setShouldLogOut, authenticated } = useContext(AppContext);
  const [graphqlImportHistoryNextToken, setGraphqlImportHistoryNextToken] = useState('');
  const [importHistoryPageState, setImportHistoryPageState] = useState(initPageState);
  const hasFetchedImportHistoryData = useRef(false);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(initColumnVisibilityModel);
  const [importHistorySortModel, setImportHistorySortModel] = React.useState(defaultRowSort);
  const [importHistoryFilterModel, setImportHistoryFilterModel] = React.useState({ items: [] });
  const typeColumns = getColumnsForType(type);
  const [orderedColumns, setOrderedColumns] = useState(typeColumns.slice(0));

  const getDefaultColumns = () => typeColumns;

  const SIGNED_URL_EXPIRY_MS = 12 * 60 * 60 * 1000;

  const importTypeFilter = () => {
    if (type === 'p2p_bulk_upload') {
      const twelveHoursAgo = new Date(Date.now() - SIGNED_URL_EXPIRY_MS).toISOString();
      return {
        and: [
          { upload_type: { eq: 'p2p_bulk_upload' } },
          { timestamp: { gt: twelveHoursAgo } },
        ],
      };
    }
    if (type === 'exclusions_upload') return { upload_type: { eq: 'exclusions_upload' } };
    return null;
  };

  const fetchImportHistory = async (token = graphqlImportHistoryNextToken) => {
    const history = await APIWrapper.queryApi(
      { query: 'importHistoryByClient' },
      setShouldLogOut,
      {
        client,
        limit: 2000,
        nextToken: token ? graphqlImportHistoryNextToken : null,
        sortDirection: 'DESC',
        filter: importTypeFilter(),
      },
    )
      .then((resp) => resp)
      .catch((err) => logger.error(err));
    // we will create rows in the format we want to display in the table here
    // we will have to add import type specific fields for code bulk import
    const formedRows = createImportHistoryRows(history.data);
    setGraphqlImportHistoryNextToken(history.nextToken);
    setImportHistoryPageState((old) => ({
      ...old, isLoading: false, data: formedRows, total: formedRows.length,
    }));
  };

  function fetchBasicImportHistory() {
    if (type === 'exclusions_upload') {
      document.title = 'MissionPortal | Transaction Exclusions Upload and History';
    } else if (type === 'p2p_bulk_upload') {
      document.title = 'MissionPortal | Bulk Upload';
    }

    if (!hasFetchedImportHistoryData.current && authenticated) {
      fetchImportHistory()
        .then(() => {
          hasFetchedImportHistoryData.current = true;
        })
        .catch((e) => {
          logger.error(e);
        });
    }
  }

  const customSetOrdered = (neworder) => {
    updateTablePreferenceLocalStorage(tablePrefix, 'column', neworder);
  };

  useEffect(() => {
    fetchBasicImportHistory();
  }, []);

  useEffect(() => {
    // triggered when a user uploads a new file to dromo
    if (!importedNewData) {
      return;
    }
    setImportHistoryPageState((old) => ({
      ...old, isLoading: true,
    }));
    // fetch data for table again
    fetchImportHistory('')
      .then(() => {
        hasFetchedImportHistoryData.current = true;
        handleRefreshHandled();
      })
      .catch((e) => {
        logger.error(e);
      });
  }, [importedNewData, handleRefreshHandled]);

  useEffect(() => {
    const importHistoryColumnVisibility = getLocalStorageTablePref(tablePrefix, 'column-visibility');
    if (!importHistoryColumnVisibility) {
      updateTablePreferenceLocalStorage(tablePrefix, 'column-visibility', initColumnVisibilityModel);
    } else {
      setColumnVisibilityModel(importHistoryColumnVisibility);
    }
  }, []);

  useEffect(() => {
    const importHistoryRowSort = getLocalStorageTablePref(tablePrefix, 'sort');
    if (!importHistoryRowSort) {
      updateTablePreferenceLocalStorage(tablePrefix, 'sort', defaultRowSort);
    } else {
      setImportHistorySortModel(importHistoryRowSort);
    }
  }, []);

  useEffect(() => {
    tableColumnPreferenceLoader(tablePrefix, 'column', typeColumns, setOrderedColumns);
  }, [importHistoryPageState]);

  useEffect(() => {
    const importHistoryFilters = getLocalStorageTablePref(tablePrefix, 'filter');
    if (importHistoryFilters) {
      setImportHistoryFilterModel(importHistoryFilters);
    } else {
      updateTablePreferenceLocalStorage(tablePrefix, 'filter', { items: [] });
    }
  }, []);

  return (
    <Table
      columns={orderedColumns}
      setOrderedColumns={setOrderedColumns}
      columnVisibilityModel={columnVisibilityModel}
      setColumnVisibilityModel={setColumnVisibilityModel}
      updatePageState={setImportHistoryPageState}
      pageState={importHistoryPageState}
      sortModel={importHistorySortModel}
      setSortModel={setImportHistorySortModel}
      tablePrefix={tablePrefix}
      filterModel={importHistoryFilterModel}
      setFilterModel={setImportHistoryFilterModel}
      getDefaultColumns={getDefaultColumns}
      customSetOrdered={customSetOrdered}
      customRowHeight="auto"
    />
  );
}

ImportHistory.propTypes = {
  client: PropTypes.string,
  importedNewData: PropTypes.bool,
  handleRefreshHandled: PropTypes.func,
  type: PropTypes.string,
};
